import React, { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../../../../utils/supabaseErrors';
import TimeInput from '../../../../../components/TimeInput';
import DurationInput from '../../../../../components/DurationInput';
import SearchableProjectDropdown from '../../../../../components/SearchableProjectDropdown';
import type { ProjectOption } from '../../../../../components/SearchableProjectDropdown/config';
import IconButton from '../../../../../components/IconButton';
import { CheckIcon, CloseIcon } from '../../../../../components/IconButton/icons';
import { addHoursToTime, hoursBetween, LunchBreak, startAfterLunch } from '../../../../../utils/timesheetTime';
import { LEAVE_TYPE_OPTIONS, LeaveType, ProjectGroup, TimesheetEntryInput } from '../../../config';
import { describeOption } from '../../../timesheetDerive';

/** The leave types, listed ahead of the project results in the same search. */
const LEAVE_ITEMS = LEAVE_TYPE_OPTIONS.map((o) => ({ id: `leave:${o.value}`, label: o.label }));

export interface DayAddSeed {
  /** A recent-strip pick or a grid row, preselecting the project or leave. */
  group?: ProjectGroup | null;
  option?: ProjectOption | null;
  leaveType?: LeaveType | null;
}

interface DayAddRowProps {
  date: string;
  /** End of the latest timed entry on the day; the new row starts there. */
  lastEnd: string | null;
  /** Otherwise the employee's usual start, "HH:mm". */
  defaultStart: string;
  lunch: LunchBreak;
  seed: DayAddSeed | null;
  onCreate: (input: TimesheetEntryInput) => Promise<void>;
  onSeedConsumed: () => void;
}

/**
 * The always-present last row of the Day view. Start is prefilled with the
 * previous entry's end; typing an end fills the hours and typing hours fills
 * the end. Enter or the tick saves; Escape or the cross clears.
 */
const DayAddRow: React.FC<DayAddRowProps> = ({ date, lastEnd, defaultStart: usualStart, lunch, seed, onCreate, onSeedConsumed }) => {
  const [kind, setKind] = useState<'project' | 'leave'>('project');
  const [projectId, setProjectId] = useState<number | null>(null);
  const [isStage, setIsStage] = useState(false);
  const [projectLabel, setProjectLabel] = useState<string | null>(null);
  const [leaveType, setLeaveType] = useState<LeaveType>('personal');
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  const [hours, setHours] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped on reset so every field remounts and shows the cleared state,
  // even one that was focused when the reset happened (a focused field keeps
  // its own text and would otherwise hand the stale text on to the next entry).
  const [rowKey, setRowKey] = useState(0);
  const notesRef = useRef<HTMLInputElement>(null);
  const startRef = useRef<HTMLDivElement>(null);
  // The start the row filled in itself (chained from the last end, or from a
  // Recent pick), as opposed to one the user typed. Only an automatic start
  // follows the day when it changes underneath it: moving lunch to
  // 12:20-13:20 after a 12:20 end must move a chained 13:00 start to 13:20.
  const autoStart = useRef<string | null>(null);

  // After a pick, the next keystrokes finish the entry: the end time, or the
  // hours once an end is already there.
  const focusEndOrHours = () => {
    const inputs = startRef.current?.querySelectorAll<HTMLInputElement>('input') ?? [];
    (inputs[1]?.value ? inputs[2] : inputs[1])?.focus();
  };

  const reset = () => {
    setKind('project');
    setProjectId(null);
    setIsStage(false);
    setProjectLabel(null);
    setLeaveType('personal');
    setStart(null);
    autoStart.current = null;
    setEnd(null);
    setHours(null);
    setNotes('');
    setError(null);
    setRowKey((k) => k + 1);
  };

  // A pick from the Recent strip lands here with the project set and the
  // start prefilled, ready for an end time or a duration.
  useEffect(() => {
    if (!seed) return;
    const group = seed.group ?? null;
    const option = seed.option ?? null;
    const leave = seed.leaveType ?? group?.leaveType ?? null;
    if (leave) {
      setKind('leave');
      setLeaveType(leave);
      setProjectId(null);
      setProjectLabel(LEAVE_TYPE_OPTIONS.find((o) => o.value === leave)?.label ?? 'Leave');
    } else {
      setKind('project');
      const id = option ? Number(option.id) : group?.projectId ?? null;
      setProjectId(id);
      setIsStage(option ? option.entity_type === 'stage' : group?.entityType === 'stage');
      const described = option ? describeOption(option) : { code: group?.jobNumber ?? '', name: group?.projectName ?? '' };
      setProjectLabel(id ? `${described.code} · ${described.name}` : null);
    }
    const seededStart = startAfterLunch(lastEnd ?? usualStart, lunch);
    setStart(seededStart);
    autoStart.current = seededStart;
    setEnd(null);
    setHours(null);
    setError(null);
    onSeedConsumed();
    // Focus the end time so the next keystrokes finish the entry.
    setTimeout(() => startRef.current?.querySelectorAll<HTMLInputElement>('input')[1]?.focus(), 0);
  }, [seed, lastEnd, usualStart, lunch, onSeedConsumed]);

  const timed = Boolean(start && end);
  const ready = (kind === 'leave' || projectId !== null) && Boolean(hours);
  // The start the row will use if the user never types one: the placeholder.
  // A start inside the lunch break moves to the end of it.
  const defaultStart = startAfterLunch(lastEnd ?? usualStart, lunch);

  // Keep an automatic start on the day's default while nothing hangs off it
  // yet (no end typed); once an end is there the user has built on it.
  useEffect(() => {
    if (autoStart.current === null || start !== autoStart.current || end) return;
    if (start === defaultStart) return;
    autoStart.current = defaultStart;
    setStart(defaultStart);
  }, [defaultStart, start, end]);

  const commitStart = (v: string | null) => {
    setStart(v);
    autoStart.current = null;
    setError(null);
    if (v && end) {
      const h = hoursBetween(v, end, lunch);
      if (h) {
        setHours(h);
      } else if (hours) {
        // Start moved past the end: keep the duration, move the end.
        setEnd(addHoursToTime(v, hours, lunch));
      } else {
        setHours(null);
        setError(`The end must be after the ${v} start (the lunch break is not worked).`);
      }
    } else if (v && hours) {
      setEnd(addHoursToTime(v, hours, lunch));
    }
  };
  const commitEnd = (v: string | null) => {
    setEnd(v);
    if (!v) return;
    // An end with no start: the start is the one the placeholder promised.
    const from = start ?? defaultStart;
    if (!start) setStart(from);
    const h = hoursBetween(from, v, lunch);
    setHours(h);
    setError(h ? null : `The end must be after the ${from} start (the lunch break is not worked).`);
  };
  const commitHours = (v: number | null) => {
    setHours(v);
    if (v && start) setEnd(addHoursToTime(start, v, lunch));
  };

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onCreate({
        project_id: kind === 'project' ? projectId : null,
        is_stage: kind === 'project' ? isStage : null,
        leave_type: kind === 'leave' ? leaveType : null,
        date,
        hours: hours as number,
        description: notes.trim() || null,
        start_time: timed ? start : null,
        end_time: timed ? end : null
      });
      // Chain: the next entry starts where this one ended (after lunch, if
      // that is inside the break).
      const nextStart = timed && end ? startAfterLunch(end, lunch) : null;
      reset();
      if (nextStart) {
        setStart(nextStart);
        autoStart.current = nextStart;
      }
    } catch (err) {
      setError(errorMessage(err, 'The entry could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  // Enter in any field: the field's own handler commits first (it blurs),
  // React re-renders, and only then do we read whether the row is ready --
  // through a ref, because this closure's `ready` is from before the commit.
  const latest = useRef({ ready, save });
  latest.current = { ready, save };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setTimeout(() => {
        if (latest.current.ready) latest.current.save();
      }, 0);
    } else if (e.key === 'Escape') {
      reset();
    }
  };

  return (
    <>
      <tr className={`day-view__row day-view__add ${kind === 'leave' ? 'is-leave' : ''}`} ref={startRef as React.RefObject<HTMLTableRowElement>}>
        <td className="day-view__dot-col">
          <span className="day-view__plus" aria-hidden="true">
            +
          </span>
        </td>
        <td className="day-view__time-col">
          <TimeInput key={rowKey} value={start} onCommit={commitStart} aria-label="Start time" placeholder={defaultStart} onKeyDown={onKeyDown} />
        </td>
        <td className="day-view__time-col">
          <TimeInput key={rowKey} value={end} onCommit={commitEnd} aria-label="End time" onKeyDown={onKeyDown} />
        </td>
        <td className="day-view__hours-col">
          <DurationInput key={rowKey} value={hours} onCommit={commitHours} aria-label="Hours" disabled={timed} onKeyDown={onKeyDown} />
        </td>
        <td className="day-view__project-col">
          <div className="day-view__add-project form-ui">
            {projectLabel ? (
              <button
                type="button"
                className={`day-view__add-picked ${kind === 'leave' ? 'is-leave' : ''}`}
                title="Change"
                onClick={() => {
                  setKind('project');
                  setProjectId(null);
                  setProjectLabel(null);
                }}
              >
                {projectLabel}
              </button>
            ) : (
              <SearchableProjectDropdown
                key={rowKey}
                value={null}
                minSearchLength={1}
                leadingItems={LEAVE_ITEMS}
                onChange={(id, option) => {
                  const picked = String(id ?? '');
                  if (!start) setStart(defaultStart);
                  if (picked.startsWith('leave:')) {
                    const leave = picked.slice('leave:'.length) as LeaveType;
                    setKind('leave');
                    setLeaveType(leave);
                    setProjectId(null);
                    setProjectLabel(LEAVE_TYPE_OPTIONS.find((o) => o.value === leave)?.label ?? 'Leave');
                    setTimeout(() => focusEndOrHours(), 0);
                    return;
                  }
                  setKind('project');
                  setProjectId(id ? Number(id) : null);
                  setIsStage(option?.entity_type === 'stage');
                  if (option) {
                    const { code, name } = describeOption(option);
                    setProjectLabel(`${code} · ${name}`);
                    setTimeout(() => focusEndOrHours(), 0);
                  }
                }}
                placeholder="Project, stage or leave…"
              />
            )}
          </div>
        </td>
        <td className="day-view__notes-col">
          <input
            ref={notesRef}
            type="text"
            className="text-input text-input--sm day-view__notes"
            value={notes}
            placeholder="Notes"
            aria-label="Notes"
            onChange={(e) => setNotes(e.target.value)}
            onKeyDown={onKeyDown}
          />
        </td>
        <td className="ui-table__actions">
          <div className="row-actions is-visible">
            <IconButton icon={<CheckIcon size={14} />} label="Add entry" size="sm" tone="accent" disabled={!ready || saving} onClick={save} />
            <IconButton icon={<CloseIcon size={14} />} label="Clear" size="sm" onClick={reset} />
          </div>
        </td>
      </tr>
      {error && (
        <tr className="day-view__add-error">
          <td colSpan={7}>
            <p className="field__error" role="alert">
              {error}
            </p>
          </td>
        </tr>
      )}
    </>
  );
};

export default DayAddRow;
